import { Component, EventEmitter, inject, Input, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { CompositeArticleDetailModel } from 'src/app/models/preliminaries/composite-article-detail.model';
import { CompositeArticleModel } from 'src/app/models/preliminaries/composite-article.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { CompositeArticleDetailService } from 'src/app/services/preliminaries/composite-article-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-composite-article-detail',
  templateUrl: './register-composite-article-detail.component.html',
  styleUrl: './register-composite-article-detail.component.scss',
  standalone: false,
})
export class RegisterCompositeArticleDetailComponent {

  private readonly articleService = inject(ArticleService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly compositeArticleDetailService = inject(CompositeArticleDetailService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  @Input() compositeArticleDetailContract: CompositeArticleDetailModel = new CompositeArticleDetailModel();
  @Input() compositeArticleModel: CompositeArticleModel = new CompositeArticleModel();
  @Input() compositeArticleDetails: CompositeArticleDetailModel[] = [];
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() updateListDetail = new EventEmitter<boolean>(false);

  codeNameArticle: string = '';
  formCompositeArticleDetail: FormGroup = new FormGroup({});
  compositeArticleDetailId: number = 0;

  async ngOnChanges(changes: SimpleChanges) {
    this.validateForm(new CompositeArticleDetailModel());
    const { compositeArticleDetails, compositeArticleDetailContract } = changes;
    if (compositeArticleDetails?.firstChange === false) {
      await this.createDetails(this.compositeArticleDetails);
      return;
    }
    if (compositeArticleDetailContract?.firstChange === false) {
      await this.getArticle(this.compositeArticleDetailContract.articleId);
      if (this.compositeArticleDetailContract.compositeArticleDetailId === 0) {
        this.createNewDetail();
        return;
      }
      this.validateForm(this.compositeArticleDetailContract);
      this.compositeArticleDetailId = this.compositeArticleDetailContract.compositeArticleDetailId;
    }
  }

  clearForm() {
    this.compositeArticleDetailId = 0;
    this.codeNameArticle = '';
    this.isFormView.emit(false);
    this.validateForm(new CompositeArticleDetailModel());
  }

  createNewDetail() {
    this.compositeArticleDetailId = 0;
    this.validateForm(this.compositeArticleDetailContract);
  }

  async createDetails(compositeArticleDetails: CompositeArticleDetailModel[]) {
    try {
      await firstValueFrom(
        this.compositeArticleDetailService.createDetails(compositeArticleDetails)
      );
      this.toastHandlerService.showToast(
        'success',
        'El despiece del artículo compuesto fue creado exitosamente.'
      );
      this.updateListDetail.emit(true);
    } catch (error) {
      console.error('Error al crear el despiece del artículo compuesto.', error);
    }
  }

  async createCompositeArticleDetail(compositeArticleDetailModel: CompositeArticleDetailModel) {
    try {
      await firstValueFrom(
        this.compositeArticleDetailService.create(compositeArticleDetailModel)
      );
      this.toastHandlerService.showToast(
        'success',
        'El despiece del artículo compuesto fue creado exitosamente.'
      );
      this.updateListDetail.emit(true);
      this.clearForm();
    } catch (error) {
      console.error('Error al crear el despiece del artículo compuesto.', error);
    }
  }

  async getArticle(articleId: number) {
    try {
      const article = await firstValueFrom(
        this.articleService.getArticleById(articleId)
      );
      this.codeNameArticle = `${article.code} - ${article.name}`;
    } catch (error) {
      console.error('Error al obtener el artículo despiece por Id.', error);
    }
  }

  async send() {
    this.validateFormControls();
    if (!this.formCompositeArticleDetail.valid) {
      this.showValidationMessagesDetail();
      return;
    }
    const model = this.formCompositeArticleDetail.getRawValue() as CompositeArticleDetailModel;
    model.compositeArticleId = this.compositeArticleModel.articleId;
    if (this.compositeArticleDetailId == 0) {
      await this.createCompositeArticleDetail(model);
    } else {
      await this.updateCompositeArticleDetail(model);
    }
  }

  showValidationMessagesDetail() {
    const controlMessages: Record<string, Record<string, string>> = {
      compositeArticleId: {
        required: 'El campo Artículo Compuesto es obligatorio.',
        min: 'Debes seleccionar un Artículo Compuesto válido.'
      },
      articleId: {
        required: 'El campo Artículo despiece es obligatorio.',
        min: 'Debes seleccionar un Artículo despiece válido.'
      },
      quantity: {
        required: 'El campo Cantidad es obligatorio.'
      }
    };
    Object.keys(controlMessages).forEach(control => {
      const formControl = this.formCompositeArticleDetail.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  async updateCompositeArticleDetail(modelCompositeArticleDetail: CompositeArticleDetailModel) {
    try {
      this.compositeArticleDetailContract.quantity = modelCompositeArticleDetail.quantity;
      await firstValueFrom(
        this.compositeArticleDetailService.update(this.compositeArticleDetailContract)
      );
      this.toastHandlerService.showToast(
        'success',
        `El despiece del artículo compuesto fue modificado exitosamente.`
      );
      this.updateListDetail.emit(true);
      this.clearForm();
    } catch (error) {
      console.error('Error al modificar el despiece del artículo compuesto.', error);
    }
  }

  validateForm(compositeArticleDetailModel: CompositeArticleDetailModel) {
    this.formCompositeArticleDetail = this.formBuilder.group({
      compositeArticleId: [compositeArticleDetailModel.compositeArticleId, [Validators.required, Validators.min(1)]],
      articleId: [compositeArticleDetailModel.articleId, [Validators.required]],
      quantity: [compositeArticleDetailModel.quantity, [Validators.required, Validators.min(0)]],
    });
  }

  validateFormControls() {
    Object.keys(this.formCompositeArticleDetail.controls).forEach((key) => {
      const control = this.formCompositeArticleDetail.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
